import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useFacility } from "@/context/FacilityContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { Users, Boxes, Stethoscope, Layers, Hospital, Loader2, MapPin, BedDouble } from "lucide-react";
import { MODULE_LABELS } from "@/lib/modules";

export default function TenantDashboard() {
  const { activeId, activeFacility } = useFacility();
  const { auth } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!activeId) return;
    setData(null);
    api.get(`/tenant/dashboard?facility_id=${activeId}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [activeId]);

  if (!activeId) return <div className="text-slate-400 py-20 text-center">No facility available for your account.</div>;
  if (data === null) return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;
  if (data === false) return <div className="text-slate-400 py-20 text-center">Unable to load dashboard.</div>;

  return (
    <div data-testid="tenant-dashboard">
      <PageHeader title={`Welcome, ${auth?.user?.name?.split(" ")[0] || ""}`} subtitle={`Operational overview for ${data.facility.name}.`} />

      <div className="bg-slate-900 text-white rounded-md p-6 mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <span className="h-12 w-12 rounded-md bg-blue-600 grid place-items-center shrink-0"><Hospital className="h-6 w-6" /></span>
        <div className="flex-1">
          <div className="font-display text-xl font-semibold">{data.facility.name}</div>
          <div className="text-sm text-slate-400 flex flex-wrap items-center gap-4 mt-1">
            <span className="font-mono text-xs">{data.facility.code}</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {data.facility.city}, {data.facility.state}</span>
            <span className="inline-flex items-center gap-1.5"><BedDouble className="h-3.5 w-3.5" /> {data.facility.beds} beds</span>
          </div>
        </div>
        <div className="text-right"><div className="text-[11px] font-mono uppercase text-slate-500">Organisation</div><div className="text-sm font-medium">{data.organisation?.name}</div></div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Active Users" value={data.active_users} icon={Users} testId="tenant-stat-users" />
        <StatCard label="Departments" value={data.departments} icon={Boxes} tone="emerald" testId="tenant-stat-departments" />
        <StatCard label="Services" value={data.services} icon={Stethoscope} tone="amber" testId="tenant-stat-services" />
        <StatCard label="Enabled Modules" value={data.enabled_modules_count} icon={Layers} tone="slate" testId="tenant-stat-modules" />
      </div>

      <div className="bg-white border border-slate-200 rounded-md mt-6">
        <div className="px-5 py-3.5 border-b border-slate-200"><h3 className="font-display font-semibold text-slate-800">Enabled Modules</h3></div>
        <div className="p-5 flex flex-wrap gap-2">
          {data.enabled_modules.map((code) => (
            <span key={code} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200 text-sm font-medium" data-testid={`enabled-module-${code}`}>
              <Layers className="h-3.5 w-3.5" /> {MODULE_LABELS[code] || code}
            </span>
          ))}
          {data.enabled_modules.length === 0 && <span className="text-sm text-slate-400">No modules enabled for this facility.</span>}
        </div>
      </div>
    </div>
  );
}
